import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import ProductModal from '../components/ProductModal';

const emptyProduct = {
  name: '',
  category: '',
  category_id: '',
  short_desc: '',
  full_desc: '',
  price: '',
  sizes: '',
  sku: '',
  badge: '',
  image_url: '',
  image_url_2: '',
  image_url_3: '',
  active: true,
};

export default function AdminPanel() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [previewProduct, setPreviewProduct] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  async function loadProducts() {
    const { data, error } = await supabase
      .from('products')
      .select('*, categories(name, slug)')
      .order('created_at', { ascending: false });

    if (error) setMessage(error.message);
    setProducts(data || []);
  }

  async function loadCategories() {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (error) setMessage(error.message);
    setCategories(data || []);
  }

  const filteredProducts = useMemo(() => {
    const term = search.toLowerCase().trim();

    if (!term) return products;

    return products.filter((product) => {
      const name = product.name || '';
      const sku = product.sku || '';
      const categoryName = product.categories?.name || '';
      const categorySlug = product.category || '';

      return (
        name.toLowerCase().includes(term) ||
        sku.toLowerCase().includes(term) ||
        categoryName.toLowerCase().includes(term) ||
        categorySlug.toLowerCase().includes(term)
      );
    });
  }, [products, search]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function uploadImage(file, currentUrl = '') {
    if (!file) return currentUrl;

    const extension = file.name.split('.').pop();
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;

    const { error } = await supabase.storage
      .from('product-images')
      .upload(path, file);

    if (error) throw error;

    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(path);

    return data.publicUrl;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage('Guardando...');

    try {
      const image1 = event.target.image.files[0];
      const image2 = event.target.image_2.files[0];
      const image3 = event.target.image_3.files[0];

      const imageUrl1 = await uploadImage(image1, form.image_url);
      const imageUrl2 = await uploadImage(image2, form.image_url_2);
      const imageUrl3 = await uploadImage(image3, form.image_url_3);

      const selectedCategory = categories.find((cat) => cat.id === form.category_id);

      const payload = {
        ...form,
        short_desc: '',
        category: selectedCategory?.slug || form.category,
        image_url: imageUrl1,
        image_url_2: imageUrl2,
        image_url_3: imageUrl3,
      };

      if (!editingId) {
        delete payload.sku;
      }

      const response = editingId
        ? await supabase.from('products').update(payload).eq('id', editingId)
        : await supabase.from('products').insert(payload);

      if (response.error) throw response.error;

      setForm(emptyProduct);
      setEditingId(null);
      event.target.reset();
      setMessage('Producto guardado correctamente.');
      loadProducts();
    } catch (error) {
      setMessage(error.message);
    }
  }

  function editProduct(product) {
    setEditingId(product.id);

    setForm({
      name: product.name || '',
      category: product.category || '',
      category_id: product.category_id || '',
      short_desc: '',
      full_desc: product.full_desc || product.short_desc || '',
      price: product.price || '',
      sizes: product.sizes || '',
      sku: product.sku || '',
      badge: product.badge || '',
      image_url: product.image_url || '',
      image_url_2: product.image_url_2 || '',
      image_url_3: product.image_url_3 || '',
      active: Boolean(product.active),
    });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function deleteProduct(id) {
    const confirmed = window.confirm('¿Seguro que quieres eliminar este producto?');
    if (!confirmed) return;

    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);

    if (error) setMessage(error.message);
    loadProducts();
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  return (
    <main className="adminPage">
      <div className="adminHeader">
        <div>
          <p className="eyebrow">Panel privado</p>
          <h1>Administrador de productos</h1>
        </div>

        <div className="adminActions">
          <a className="secondaryButton" href="/">Ver catálogo</a>
          <button className="secondaryButton" onClick={signOut}>Salir</button>
        </div>
      </div>

      <form className="adminCard" onSubmit={handleSubmit}>
        <h2>{editingId ? 'Editar producto' : 'Nuevo producto'}</h2>

        <div className="formGrid">
          <label>
            Nombre
            <input
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />
          </label>

          <label>
            Categoría
            <select
              value={form.category_id}
              onChange={(e) => {
                const selectedCategory = categories.find((cat) => cat.id === e.target.value);
                updateField('category_id', e.target.value);
                updateField('category', selectedCategory?.slug || '');
              }}
              required
            >
              <option value="">Selecciona una categoría</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Precio
            <input
              value={form.price}
              onChange={(e) => updateField('price', e.target.value)}
              placeholder="Ej: 20"
            />
          </label>

          <label>
            Referencia automática
            <input
              value={form.sku || 'Se genera automáticamente'}
              readOnly
            />
          </label>

          <label>
            Tallas disponibles
            <input
              value={form.sizes}
              onChange={(e) => updateField('sizes', e.target.value)}
              placeholder="Ej: 2 años, 4 años, 6 años, S, M, L, XL"
            />
          </label>

          <label>
            Badge
            <input
              value={form.badge}
              onChange={(e) => updateField('badge', e.target.value)}
              placeholder="Nuevo, Oferta..."
            />
          </label>
        </div>

        <label>
          Descripción
          <textarea
            value={form.full_desc}
            onChange={(e) => updateField('full_desc', e.target.value)}
            placeholder="Describe el producto, materiales, personalización, medidas, etc."
          />
        </label>

        <label>
          Imagen principal
          <input name="image" type="file" accept="image/*" />
        </label>
        {form.image_url && (
          <img src={form.image_url} alt="Vista actual 1" className="preview" />
        )}

        <label>
          Imagen 2
          <input name="image_2" type="file" accept="image/*" />
        </label>
        {form.image_url_2 && (
          <img src={form.image_url_2} alt="Vista actual 2" className="preview" />
        )}

        <label>
          Imagen 3
          <input name="image_3" type="file" accept="image/*" />
        </label>
        {form.image_url_3 && (
          <img src={form.image_url_3} alt="Vista actual 3" className="preview" />
        )}

        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => updateField('active', e.target.checked)}
          />
          Producto activo
        </label>

        <div className="adminActions">
          <button className="primaryButton" type="submit">
            Guardar producto
          </button>

          {editingId && (
            <button
              className="secondaryButton"
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(emptyProduct);
              }}
            >
              Cancelar edición
            </button>
          )}
        </div>

        {message && <p className="message">{message}</p>}
      </form>

      <section className="adminCard">
        <h2>Productos</h2>

        <label>
          Buscar producto
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, categoría o referencia INK001"
          />
        </label>

        <div className="adminList">
          {filteredProducts.map((product) => (
            <div className="adminItem" key={product.id}>
              {product.image_url && (
                <img src={product.image_url} alt={product.name} />
              )}

              <div>
                <strong>{product.name}</strong>
                <p>
                  {product.sku || 'Sin ref'} · {product.price} € ·{' '}
                  {product.categories?.name || product.category || 'Sin categoría'} ·{' '}
                  {product.active ? 'Activo' : 'Oculto'}
                </p>
              </div>

              <button
  className="primaryButton"
  onClick={() => setPreviewProduct(product)}
>
  Ver
</button>

              <button
                className="secondaryButton"
                onClick={() => editProduct(product)}
              >
                Editar
              </button>

              <button
                className="dangerButton"
                onClick={() => deleteProduct(product.id)}
              >
                Eliminar
              </button>
            </div>
          ))}

          {filteredProducts.length === 0 && (
            <p>No se encontraron productos.</p>
          )}
        </div>
      </section>


      {previewProduct && (
        <ProductModal
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
        />
      )}

    </main>
  );
}